import Validator from 'validatorjs';
import cardValidator from "card-validator";
import { eventEmitter } from "../classes/EventEmitter";
import { EVENTS, VALIDATION } from "../AppConstants";
import * as _ from 'lodash';
import USPSClient from "../classes/USPSClient";
import { IUSPSResult } from '../classes/Domain';
import Utilities from '../classes/Utilities';
import { QUOTE } from "../AppConstants";

export interface IValidationFailure {
    property: string,
    message : string;
}

interface IValidationResult {
    isValid: boolean | void,
    errors : Validator.ValidationErrors | null;
}

export const allFieldsValidationCommon = (data: any, rules: any, customErrorMessages?: any) => {
    const validation = new Validator(data, rules, customErrorMessages ? customErrorMessages : customErrorMessagesCommon);

    const validationResponse: IValidationResult = {
        isValid: validation.passes(),
        errors: null
    };

    if (!validationResponse.isValid) {
        validationResponse.errors = validation.errors.all();
    }
    return validationResponse;
};

export const collectionValidation = (data: Array<any>, dataItemKeySelector: (item: any) => any, rules: any, customErrorMessages?: any) => {
    const validationResponseAll = { isValid: true, errors: {} };
    for (var i = 0; i < data.length; i++) {
        const validationResponse = allFieldsValidationCommon(data[i], rules, customErrorMessages);
        if (validationResponse.isValid) continue;
        validationResponseAll.isValid = false;
        validationResponseAll.errors[dataItemKeySelector(data[i])] = validationResponse.errors;
    }

    return validationResponseAll;
};

export const customErrorMessagesCommon = {
    required: VALIDATION.REQUIRED,
    required_if: VALIDATION.REQUIRED,
    email: VALIDATION.INVALID_EMAIL,
    valid_date: VALIDATION.INVALID_DATE,
    valid_mm_yy_date: VALIDATION.INVALID_DATE,
    valid_phone: VALIDATION.INVALID_PHONE,
    valid_insured_age: VALIDATION.INVALID_INSURED_AGE,
    valid_driver_age: VALIDATION.INVALID_DRIVER_AGE,
    valid_getjerry_email: VALIDATION.INVALID_GETHERRY_EMAIL
};

Validator.register('valid_credit_card_number', function (value, requirement, attribute) {
    let check = cardValidator.number(value, { luhnValidateUnionPay: true });
    return check.isValid;
});

function parseDate(dateStr: string) {
    if (!dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) return null;
    const [m, d, y] = dateStr.split('/');

    const month = parseInt(m, 10);
    const day = parseInt(d, 10);
    const year = parseInt(y, 10);

    return new Date(year, month - 1, day);
}

Validator.register('valid_getjerry_email', function (value, requirement, attribute) {
    var email = value.toString();
    var check = !email.toLowerCase().includes('@getjerry.com');
    return check;
});

Validator.register('valid_date', function (value, requirement, attribute) {    
    const dateStr = value.toString();
    if (!dateStr.match(/^\d{2}\/\d{2}\/\d{4}$/)) return false;
    const [m, d, y] = dateStr.split('/');

    const month = parseInt(m, 10);
    const day = parseInt(d, 10);
    const year = parseInt(y, 10);

    if (1 > month || 12 < month) return false;

    var monthLength = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    // Adjust for leap years
    if (year % 400 == 0 || (year % 100 != 0 && year % 4 == 0))
        monthLength[1] = 29;

    // Check the range of the day
    return day > 0 && day <= monthLength[month - 1];
});

Validator.register('valid_insured_age', function (value, requirement, attribute) {
    const dateStr = value.toString();

    const birthDate = parseDate(dateStr);
    if (null === birthDate) return false;
    const age = Utilities.getAge(birthDate);
    
    return age >= QUOTE.MIN_INSURED_DRIVER_AGE;
});

Validator.register('valid_driver_age', function (value, requirement, attribute) {
    const dateStr = value.toString();

    const birthDate = parseDate(dateStr);
    if (null === birthDate) return false;
    const age = Utilities.getAge(birthDate);
    
    return age >= QUOTE.MIN_DRIVER_AGE;
});

Validator.register('valid_mm_yy_date', function (value, requirement, attribute) {
    const dateStr = value.toString();
    if (!dateStr.match(/^\d{2}\/\d{2}$/)) return false;
    const [m] = dateStr.split('/');

    const month = parseInt(m, 10);
    return (1 <= month && 12 >= month);
});

Validator.register('valid_phone', function (value, requirement, attribute) {
    const phoneStr = value.toString().replace(/[^\d]/gi, "");
    if (0 === phoneStr.length) return true;
    return (10 === phoneStr.length);
});


Validator.register('valid_credit_card_exp_date', function (value, requirement, attribute) {
    const dateStr = value.toString();
    if (!dateStr.match(/^\d{2}\/\d{2}$/)) return false;
    const [m, y] = dateStr.split('/');

    const month = parseInt(m, 10);
    const year = 2000 + parseInt(y, 10);

    if (1 > month || 12 < month) return true;

    var expDate = new Date(year, month - 1);
    return expDate > new Date();
});

Validator.registerAsync(
    'valid_zip',
    (value, args, attribute, passes) => {
        USPSClient.getAreaByZip(value.toString())
            .then((result: IUSPSResult) => {
                if (result.state.toUpperCase() === "CA") {
                    passes();
                }
                else {
                    passes(false, `Only CA zip codes are accepted`);
                }
            },
            () => passes(false, `Invalid zip code`));
        //USPSClient.cityStateLookupByZip(value.toString(), (err, result) => {
        //    if (err == null) {
        //        passes();
        //    } else {
        //        passes(false, `Invalid zip code`);
        //    }
        //});
    }, 
    ""
);

class ValidationUtils  {

    subscribeToValidationFailure(uid: string, callback: (validationData: any) => void) {
        eventEmitter.subscribe(EVENTS.VALIDATION_FAILURE, uid, callback);
    }

    unsubscribeFromValidationFailure(uid: string) {
        eventEmitter.unsubscribe(EVENTS.VALIDATION_FAILURE, uid);
    }

    publishValidationFailure(validationResult: any) {
        eventEmitter.publish(EVENTS.VALIDATION_FAILURE, validationResult);
    }

    publishValidationFailureByUid(validationResult: any, uid: string) {
        eventEmitter.publishByUid(EVENTS.VALIDATION_FAILURE, uid, validationResult);
    }

    translateServerValidationResult(errors: Array<IValidationFailure>) {
        const validationResult = {};
        const propertyGroups = _.groupBy(errors, "property");
        _.map(propertyGroups, (records, property) => {
            validationResult[property] = _.map(records, (r) => r.message);
        });
        return validationResult;
    }

    hasValidationErrorFor(errors: any, property: string) {
        return null != errors && errors.hasOwnProperty(property);
    }

    getValidationErrorFor(errors: any, ...properties) {
        if (!errors) return "";
        for (var i = 0; i < properties.length; i++) {
            if (errors.hasOwnProperty(properties[i])) {
                return errors[properties[i]][0];
            }
        }

        return "";
    }

    getValidationErrorForEntity(entityUid: any, errors: any, ...properties) {
        if (!errors) return "";
        if (!errors.hasOwnProperty(entityUid)) return "";
        var errorsForProperties = errors[entityUid];
        for (var i = 0; i < properties.length; i++) {
            if (errorsForProperties.hasOwnProperty(properties[i]))
                return errorsForProperties[properties[i]][0];
        }

        return "";
    }

    setValidationErrorFor(errors: any, property, message) {
        if (!errors) return "";
        errors[property] = message;
    }

    extractErrorsByPrefix(errors: any, prefix: string) {
        if (null === errors) return null;
        const extractedErrors = {};

        for (let propertyName in errors) {
            if (Object.prototype.hasOwnProperty.call(errors, propertyName)) {
                if (propertyName.startsWith(prefix)) {
                    extractedErrors[propertyName.replace(prefix, "")] = errors[propertyName];
                }
            }
        }

        return 0 < Object.keys(extractedErrors).length ? extractedErrors : null;
    }

    extractFirstErrorByPrefix(errors: any, prefix: string, ...properties) {
        if (null === errors) return null;
        
        for (var i = 0; i < properties.length; i++) {
            let propertyName = properties[i];
            if (Object.prototype.hasOwnProperty.call(errors, prefix + propertyName)) {
                return errors[prefix + propertyName];
            }
        }

        return null;
    }
};

export const validationUtils = new ValidationUtils();

class AppValidator {
    validate(data: any, rules: any, customErrorMessages?: any) {
        return new Promise((resolve, reject) => {
            const validator = new Validator(data, rules, customErrorMessages ? customErrorMessages : customErrorMessagesCommon);
            const handleFails = () => reject(validator.errors.all());

            // Asynchronous handler (with callbacks)
            //console.log("validator.hasAsync=" + validator.hasAsync);
            if (validator.hasAsync) {
                //validator.passes(() => resolve());
                //validator.fails(() => handleFails());
                validator.checkAsync(() => resolve(), () => handleFails());
            } else {
                // Synchronous handler
                if (validator.passes()) {
                    //console.log("appValidator passes");
                    resolve();
                } else {
                    //console.log("appValidator fails");
                    handleFails();
                }
            }
        });
    }
}

export const appValidator = new AppValidator();